/**
 * Build the expression keyboard's environment from what a surface already has:
 * its resources (flow resources incl. parameter mirrors, the file's globals,
 * loop counters, …) and the library catalogue (useLibraryFunctions, which lists
 * the project's own value-returning flows first under "Your flows").
 */
import { createEnv } from './engine';
import { getStructMembers, findStructDefinition } from '../VariablePicker';

const TYPE_DEFS = ['struct_type', 'struct_definition', 'union_type', 'enum_type', 'typedef', 'function_pointer'];
const CONSTANTS = ['macro', 'constant'];

const rtOf = (r) => (r.resourceType || r.type || '').toLowerCase();

function typeOf(r) {
  const rt = rtOf(r);
  const base = r.dataType || r.elementType || '';
  if (rt === 'array_1d' || rt === 'array') return `${r.elementType || r.dataType || 'int'}[]`;
  if (rt === 'array_2d') return `${r.elementType || r.dataType || 'int'}[][]`;
  if (rt === 'pointer') return /\*$/.test(base) ? base : `${r.pointsToType || base || 'int'}*`;
  if ((rt === 'struct_instance' || rt === 'union_instance') && r.structTypeRef) return `struct ${r.structTypeRef}`;
  return base || 'int';
}

function fieldsOf(r, all) {
  try {
    const members = getStructMembers(r, all) || [];
    if (!members.length) return { fields: null, arrow: false };
    return {
      fields: members.map((m) => ({ name: m.name, type: m.dataType })),
      arrow: String(members[0].accessPath || '').startsWith('->'),
    };
  } catch (e) {
    return { fields: null, arrow: false };
  }
}

function elementFieldsOf(r, all) {
  const rt = rtOf(r);
  if (!['array_1d', 'array', 'array_2d'].includes(rt)) return null;
  const tag = r.structTypeRef || String(r.elementType || r.dataType || '').replace(/^struct\s+/, '').replace(/\[.*$/, '').trim();
  if (!tag) return null;
  const def = findStructDefinition(tag, all);
  const members = (def && (def.structMembers?.length ? def.structMembers : def.members)) || [];
  return members.length ? members.map((m) => ({ name: m.name || m.id, type: m.dataType || m.type })) : null;
}

/**
 * @param {object} p
 * @param {Array} p.resources  every resource in scope at the edited node
 * @param {Array} p.functions  [{ name, params?, minParams?, variadic?, category, returnType }]
 */
// `flows`: the project's own flows. Any of them can be passed or stored as a value — a function
// pointer, as in mini-Postgres's command table (`commands[k].run = doCreate`) — void ones included.
export function buildKeyboardEnv({ resources = [], functions = [], flows = [] } = {}) {
  const variables = [];
  const constants = [];
  const types = [];
  const seen = new Set();
  (resources || []).forEach((r) => {
    if (!r || !r.name) return;
    const rt = rtOf(r);
    if (TYPE_DEFS.includes(rt)) {
      types.push(String(r.name).replace(/^struct_/, ''));
      if (rt === 'enum_type' && Array.isArray(r.enumMembers)) {
        r.enumMembers.forEach((m) => { if (m && m.name) constants.push({ name: m.name.trim(), type: r.name }); });
      }
      return;
    }
    if (seen.has(r.name)) return;
    seen.add(r.name);
    if (CONSTANTS.includes(rt)) { constants.push({ name: r.name, type: r.dataType || '' }); return; }
    const { fields, arrow } = fieldsOf(r, resources);
    variables.push({ name: r.name, type: typeOf(r), fields, arrow, elementFields: elementFieldsOf(r, resources) });
  });

  // printf-like calls take any number of values after their fixed ones,
  // whatever a catalogue entry says.
  const VARIADIC = ['printf', 'fprintf', 'sprintf', 'snprintf', 'scanf', 'fscanf', 'sscanf'];
  const fns = {};
  (functions || []).forEach((f) => {
    if (!f || !f.name || /\./.test(f.name)) return;
    if (fns[f.name]) return;
    fns[f.name] = {
      params: Array.isArray(f.params) ? f.params : null,
      minParams: f.minParams,
      variadic: !!f.variadic || VARIADIC.includes(f.name),
      group: f.category || 'Library',
      // A call that gives back nothing (free, rewind, a void flow) is a whole
      // Action on its own, never a value inside an expression.
      returnsNothing: (f.returnType || '').trim() === 'void',
      // What it gives back (the kind of a value, utils/expressionKind.js).
      returnType: (f.returnType || '').trim() || null,
    };
  });

  (flows || []).forEach((f) => {
    if (!f || !f.name || f.isEntry || f.flowType === 'contract') return;
    // Callable too — void flows included, for an Action (`freeImage(img)`).
    if (!fns[f.name]) {
      const params = Array.isArray(f.parameters) ? f.parameters.map((p) => (typeof p === 'string' ? p : p && p.name)).filter(Boolean) : null;
      fns[f.name] = {
        params, variadic: false, group: 'Your flows',
        returnsNothing: (f.returnType || 'void').trim() === 'void',
        returnType: (f.returnType || 'void').trim(),
      };
    }
    if (seen.has(f.name)) return;
    seen.add(f.name);
    constants.push({ name: f.name, type: 'flow' });
  });

  return createEnv({ variables, constants, functions: fns, types });
}
